/* ============================================================================
 * АУТЕНТИФИКАЦИЯ КОНСОЛИ ИВО (задача №12)
 *
 * Модель сессии — серверная, ровно как в старой консоли (ui/console/auth.js):
 *   POST /api/auth/login  ->  консоль кладёт sid в HttpOnly-cookie utm_sid
 *   GET  /api/auth/me     ->  { authenticated, roles, role_labels, expires_in }
 *   POST /api/auth/logout ->  cookie зачищается
 *
 * Токен Keycloak в браузер НЕ попадает: cookie HttpOnly + Secure. Поэтому
 * фронтенд НИКОГДА не трогает document.cookie — cookie уезжает сама, вместе
 * с запросом. Это важно: при Secure-cookie попытка прочитать её из JS вернула
 * бы пустую строку и сломала логин на ровном месте.
 *
 * credentials: 'include' выставлен на всех запросах. На текущей схеме (консоль
 * на том же origin) он избыточен, но нужен, если /api/* когда-нибудь уедет на
 * отдельный хост: без него fetch на другой origin не приложил бы cookie и все
 * маршруты молча вернули бы 401.
 *
 * ПРОВЕРЕНО 2026-10-04: cookie ставится с Path=/, поэтому покрывает и /app/,
 * где раздаётся фронтенд. Отдельного обмена с гейтом не требуется.
 * ========================================================================= */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

import { LoginScreen } from '../components/auth/LoginScreen'

/** Роли ИВО, как их знает консоль (scripts/ivo_auth.py ROLE_DESCRIPTIONS) */
export type IvoRole = 'dispatcher' | 'pilot' | 'auditor'

/** Ответ GET /api/auth/me */
export interface AuthMe {
  authenticated: boolean
  auth_enabled?: boolean
  user?: string
  roles?: Array<IvoRole | string>
  role_labels?: Record<string, string>
  expires_in?: number
  client?: string
  issuer?: string
}

/** Значение хука useAuth() */
export interface AuthState {
  /** null — бэкенд ещё не ответил (первый экран, без вспышки формы) */
  me: AuthMe | null
  /** true — можно пускать в консоль (есть сессия либо auth выключен) */
  ok: boolean
  busy: boolean
  error: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

/** Таймаут логина/логаута — Keycloak password grant бывает медленным */
const TIMEOUT = 8000

async function postJson(path: string, body?: unknown): Promise<AuthMe> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT)
  try {
    const res = await fetch(path, {
      method: 'POST',
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })
    const data = (await res.json().catch(() => ({}))) as AuthMe & { error?: string }
    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`)
    return data
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Превышено время ожидания сервера авторизации')
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}

async function fetchMe(): Promise<AuthMe> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT)
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include', signal: controller.signal })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return (await res.json()) as AuthMe
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Гейт доступа к консоли. Три состояния:
 *   me === null          — бэкенд ещё не ответил, экран ожидания (не форма);
 *   auth_enabled === false — стенд без логина, пускаем всех (ok = true);
 *   иначе               — ok = authenticated.
 */
export function useAuth(): AuthState {
  const [me, setMe] = useState<AuthMe | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    const load = () =>
      fetchMe()
        .then((data) => {
          if (mounted.current) {
            setMe(data)
            setError(null)
          }
        })
        .catch((err: unknown) => {
          if (!mounted.current) return
          // Бэкенд недоступен — не рисуем форму входа (нечего вводить, некуда
          // отправлять): сообщаем об ошибке и пробуем ещё раз через 5 с.
          setError(`Сервер авторизации недоступен: ${err instanceof Error ? err.message : '—'}`)
          setTimeout(() => mounted.current && load(), 5000)
        })
    load()
    return () => {
      mounted.current = false
    }
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    setBusy(true)
    setError(null)
    try {
      await postJson('/api/auth/login', { username, password })
      const fresh = await fetchMe()
      if (mounted.current) setMe(fresh)
    } catch (err) {
      if (mounted.current) setError(err instanceof Error ? err.message : 'Ошибка входа')
      throw err
    } finally {
      if (mounted.current) setBusy(false)
    }
  }, [])

  const logout = useCallback(async () => {
    setBusy(true)
    try {
      await postJson('/api/auth/logout')
      const fresh = await fetchMe()
      if (mounted.current) setMe(fresh)
    } catch (err) {
      if (mounted.current) setError(err instanceof Error ? err.message : 'Ошибка выхода')
    } finally {
      if (mounted.current) setBusy(false)
    }
  }, [])

  const open = me === null ? true : me.auth_enabled === false ? true : me.authenticated === true
  return { me, ok: open, busy, error, login, logout }
}

/** Человеческие названия ролей — с сервера, с запасным вариантом */
export function roleLabel(roles: Array<IvoRole | string> | undefined, fallback = 'оператор'): string {
  if (!roles || roles.length === 0) return fallback
  const map: Record<string, string> = {
    dispatcher: 'Диспетчер',
    pilot: 'Пилот',
    auditor: 'Аудитор',
  }
  return roles.map((r) => map[r] ?? r).join(' · ')
}

/* ============================================================================
 * ГЕЙТ ДОСТУПА (AuthGate)
 *
 * Почему не «early return» прямо в App(): условный return перед хуками опроса
 * делает ПОРЯДОК ХУКОВ зависящим от состояния авторизации. React на это ругается
 * «Rendered more hooks than during the previous render», а при выходе/входе
 * приложение падало бы вслепую. Поэтому гейт — отдельная компонента-собственник
 * useAuth(): пока не пустил, App() вообще не монтируется и ни одного запроса
 * к источникам не улетает вхолостую.
 * ========================================================================= */

const AuthCtx = createContext<AuthState | null>(null)

/** Сессия внутри консоли. Вне гейта — ошибка разработки, а не «пропустить». */
export function useAuthSession(): AuthState {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuthSession() вызван вне <AuthGate>')
  return ctx
}

function GateLoading() {
  return (
    <div className="flex h-full items-center justify-center bg-canvas text-ink">
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 size={16} className="animate-spin" aria-hidden />
        Проверка доступа…
      </div>
    </div>
  )
}

/** Пропускает детей только при действующей сессии. */
export function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuth()
  const loading = useMemo(() => <GateLoading />, [])

  if (auth.me === null) return loading
  if (!auth.ok) return <LoginScreen auth={auth} />
  return <AuthCtx.Provider value={auth}>{children}</AuthCtx.Provider>
}